import { useEffect, useRef, useState } from "react";
import handleError from "./handleError";

type UsePaginationProps<T> = {
  fn: (_: { offset: number; limit?: number }) => Promise<{ count: number; items: T[] }>;
  initial?: boolean;
  itemsPerPage?: number;
};

const usePagination = function usePagination<T>(props: UsePaginationProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isLoadingRef = useRef(false);
  const itemsPerPage = props.itemsPerPage || 10;

  const load = async (args: { append: boolean } = { append: true }) => {
    if (args.append && data.length >= count) return;

    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    try {
      setIsLoading(true);
      const offset = args.append ? data.length : 0;
      const { count, items } = await props.fn({ offset, limit: itemsPerPage });
      setData((prev) => (args.append ? [...prev, ...items] : items));
      setCount(count);

      return items;
    } catch (err) {
      handleError(err);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  };

  const loadPage = async (page: number) => {
    if (page < 1) page = 1;
    const maxPage = Math.max(1, Math.ceil(count / itemsPerPage));
    if (page > maxPage) page = maxPage;

    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    try {
      setIsLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const { count, items } = await props.fn({ offset, limit: itemsPerPage });

      setData(items);
      setCount(count);
      setCurrentPage(page);

      return items;
    } catch (err) {
      handleError(err);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    setIsRefreshing(true);
    await load({ append: false });
    setCurrentPage(1);
    setIsRefreshing(false);
  };

  const replaceItem = (index: number, fn: (item: T) => T) =>
    setData((prev) => {
      if (index < 0 || index >= prev.length) return prev;

      const next = [...prev];
      next[index] = fn(prev[index]);

      return next;
    });

  useEffect(() => {
    if (props.initial !== false) load({ append: false });

    return () => {
      setData([]);
      setIsLoading(true);
    };
  }, []);

  return {
    count,
    data,
    isLoading,
    isRefreshing,
    load,
    loadPage,
    refresh,
    replaceItem,
    setCount,
    setData,
    currentPage,
    itemsPerPage,
  };
};

export default usePagination;
